import { Component, Input, OnInit } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { KPI } from '../../../../@core/api';
import { PerimeterEnergyTrajectoryService, PerimeterPathwayService, PerimetersService } from '../../../../@core/utils';
import { map, switchMap } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import * as R from 'ramda';

export interface MappedPerimeter extends KPI.Perimeter {
  expanded: boolean;
}

interface TransformedObjective {
  id: number;
  name: string;
  displayed: boolean;
  consumption: number[];
  emission: number[];
}

interface PerimeterPathway {
  name: string;
  expanded: boolean;
  displayed: boolean;
  monositeObjectives: TransformedObjective[];
}

@Component({
  selector: 'app-corporate-pathways',
  styleUrls: ['./corporate-pathways.component.scss'],
  templateUrl: './corporate-pathways.component.html',
  standalone: true,
  imports: [
    FontAwesomeModule,
    DecimalPipe,
    TranslateModule,
    AsyncPipe
  ],
})
export class CorporatePathwaysComponent implements OnInit {

  @Input() public objectiveType: 'energy' | 'carbon';

  public sites: MappedPerimeter[] = [];
  public startYear = new Date().getFullYear() - 3;
  public years: number[] = R.range(this.startYear, 2051);
  public perimetersPathway$: Observable<PerimeterPathway[]> = combineLatest([
    this.perimetersService.perimeters$.pipe(
      switchMap((sites) => this.perimeterPathwayService.requestPathway$(sites, this.years)),
    ),
    this.perimeterEnergyTrajectoryService.selectedTrajectoryPerimeterIds$,
  ]).pipe(
    map(([pathway, selectedPerimeterIds]) => this.parseData(pathway.site_objectives, selectedPerimeterIds)),
  )

  constructor(
    private perimetersService: PerimetersService,
    private perimeterPathwayService: PerimeterPathwayService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
  ) { }

  public ngOnInit(): void {
  }

  private parseData(siteObjectives, selectedPerimeterIds: number[]): PerimeterPathway[] {
    const isSingleBuilding = selectedPerimeterIds.length === 1;
    return siteObjectives.map((objectives) => {
      const monositeObjectives: TransformedObjective[] = objectives.monosite_objectives.map((monoObjective) => ({
        id: monoObjective.mono_perimeter.id,
        name: monoObjective.mono_perimeter.name,
        displayed: isSingleBuilding && selectedPerimeterIds.includes(monoObjective.mono_perimeter.id),
        consumption: this.years.map(year => monoObjective.objectives[year]?.consumption.toFixed(2) || null),
        emission: this.years.map(year => monoObjective.objectives[year]?.emission.toFixed(2) || null),
      }));
      const displayed = monositeObjectives.some(monosite => monosite.displayed);
      return {
        name: objectives.site.name,
        expanded: displayed,
        displayed,
        monositeObjectives,
      };
    });
  }

}
